/**
 * Tiny i18n table. No library: two dictionaries, a getter, and a subscription so
 * Preact components re-render when the language changes.
 *
 * Russian is the default UI language (the project's first audience); EN is a
 * toggle, persisted in localStorage.
 */
export type Lang = 'ru' | 'en';

const STORAGE_KEY = 'olr.lang';

export const translations = {
  ru: {
    'app.name': 'OpenLanguageReactor',
    'app.tagline': 'Самостоятельно размещённый тренажёр по субтитрам',
    'app.bootFailed': 'Не удалось связаться с сервером.',
    'app.bootHint': 'Проверьте, что API запущен и доступен по этому адресу, затем повторите.',
    'nav.vocab': 'Словарь',
    'nav.settings': 'Настройки',
    'nav.anki': 'Anki',
    'nav.logout': 'Выйти',
    'nav.lang': 'Язык интерфейса',
    'common.loading': 'Загрузка…',
    'common.save': 'Сохранить',
    'common.saving': 'Сохранение…',
    'common.cancel': 'Отмена',
    'common.delete': 'Удалить',
    'common.edit': 'Изменить',
    'common.close': 'Закрыть',
    'common.retry': 'Повторить',
    'common.yes': 'Да',
    'common.no': 'Нет',
    'common.optional': 'необязательно',
    'common.none': 'нет',
    'common.unknown': 'неизвестно',
    'common.saved': 'Сохранено',
    'common.of': 'из',
    'common.notFound': 'Страница не найдена',
    'common.notFoundHint': 'Такого раздела нет. Откройте словарь или настройки.',
    'health.ok': 'Сервер доступен',
    'health.fail': 'Сервер недоступен',
    'health.checking': 'Проверка сервера…',
    'health.base': 'Адрес сервера',
    'auth.login': 'Вход',
    'auth.register': 'Регистрация',
    'auth.email': 'Почта',
    'auth.password': 'Пароль',
    'auth.displayName': 'Имя (необязательно)',
    'auth.loginAction': 'Войти',
    'auth.registerAction': 'Создать аккаунт',
    'auth.loggingIn': 'Вход…',
    'auth.registering': 'Создание аккаунта…',
    'auth.haveAccount': 'Уже есть аккаунт? Войти',
    'auth.noAccount': 'Нет аккаунта? Зарегистрироваться',
    'auth.registerDisabled':
      'Регистрация на этом сервере закрыта. Попросите администратора создать аккаунт или включить OLR_ALLOW_REGISTRATION.',
    'auth.invalidCredentials': 'Неверная почта или пароль.',
    'auth.emailTaken': 'Аккаунт с такой почтой уже существует.',
    'auth.passwordTooShort': 'Пароль должен быть не короче 8 символов.',
    'auth.sessionExpired': 'Сессия истекла. Войдите снова.',
    'auth.loggedOut': 'Вы вышли.',
    'vocab.title': 'Словарь',
    'vocab.subtitle': 'Слова и фразы, сохранённые расширением или добавленные вручную.',
    'vocab.search': 'Поиск по слову или переводу',
    'vocab.searchHint': 'Поиск на сервере; страница дополнительно фильтруется локально.',
    'vocab.filterSource': 'Язык оригинала',
    'vocab.filterTarget': 'Язык перевода',
    'vocab.filterAll': 'все',
    'vocab.sort': 'Сортировка',
    'vocab.sortUpdatedDesc': 'Сначала изменённые',
    'vocab.sortAlpha': 'По алфавиту',
    'vocab.sortCreatedDesc': 'Сначала новые',
    'vocab.sortCreatedAsc': 'Сначала старые',
    'vocab.colWord': 'Слово',
    'vocab.colTranslation': 'Перевод',
    'vocab.colContext': 'Контекст',
    'vocab.colNote': 'Заметка',
    'vocab.colLangs': 'Языки',
    'vocab.colUpdated': 'Изменено',
    'vocab.colActions': 'Действия',
    'vocab.empty': 'Пока пусто',
    'vocab.emptyHint': 'Сохраните слово в расширении или добавьте его здесь.',
    'vocab.noMatches': 'Ничего не найдено по текущему фильтру.',
    'vocab.add': 'Добавить слово',
    'vocab.addTitle': 'Новое слово',
    'vocab.added': 'Слово добавлено',
    'vocab.updated': 'Изменения сохранены',
    'vocab.deleted': 'Удалено',
    'vocab.confirmDelete': 'Удалить «{word}»? Действие необратимо.',
    'vocab.confirmBulk': 'Удалить выбранные записи: {count}?',
    'vocab.selected': 'Выбрано: {count}',
    'vocab.selectAll': 'Выбрать все на странице',
    'vocab.clearSelection': 'Снять выделение',
    'vocab.bulkDelete': 'Удалить выбранные',
    'vocab.page': 'Страница {page} из {pages}',
    'vocab.prev': 'Назад',
    'vocab.next': 'Вперёд',
    'vocab.total': '{count} записей',
    'vocab.import': 'Импорт',
    'vocab.importHint': 'CSV, TSV или JSON. Первая строка CSV — заголовки (text, translation, context).',
    'vocab.importAction': 'Загрузить файл',
    'vocab.importResult': 'Импортировано: {imported}, пропущено: {skipped}',
    'vocab.importErrors': 'Ошибки: {count}',
    'vocab.export': 'Экспорт',
    'vocab.exportEmpty': 'Экспортировать нечего.',
    'vocab.occurrences': 'встречается: {count}',
    'vocab.wordRequired': 'Слово не может быть пустым.',
    'vocab.fieldWord': 'Слово',
    'vocab.fieldTranslation': 'Перевод',
    'vocab.fieldNote': 'Заметка',
    'settings.title': 'Настройки',
    'settings.subtitle': 'Применяются к расширению на всех устройствах этого аккаунта.',
    'settings.languages': 'Языки',
    'settings.source': 'Язык оригинала',
    'settings.target': 'Язык перевода',
    'settings.interface': 'Язык интерфейса расширения',
    'settings.provider': 'Провайдер перевода',
    'settings.playback': 'Субтитры и воспроизведение',
    'settings.fontScale': 'Размер субтитров',
    'settings.opacity': 'Непрозрачность фона',
    'settings.autoPause': 'Пауза в конце реплики',
    'settings.showNative': 'Показывать оригинальную расшифровку',
    'settings.autoSave': 'Автоматически сохранять слова',
    'settings.anki': 'Anki',
    'settings.ankiEnabled': 'Отправлять слова в Anki',
    'settings.deck': 'Колода',
    'settings.model': 'Модель',
    'settings.tags': 'Теги',
    'settings.updatedAt': 'Обновлено: {date}',
    'settings.loadError': 'Не удалось загрузить настройки.',
    'anki.title': 'Anki',
    'anki.subtitle': 'Связь с AnkiConnect и отправка слов карточками.',
    'anki.notice':
      'AnkiConnect работает на компьютере пользователя (127.0.0.1:8765), а не на сервере. Если Anki не запущен на самом сервере, статус «недоступен» — это нормально.',
    'anki.status': 'Статус',
    'anki.ping': 'Проверить связь',
    'anki.pinging': 'Проверка…',
    'anki.reachable': 'AnkiConnect доступен',
    'anki.unreachable': 'AnkiConnect недоступен',
    'anki.reason': 'Причина: {reason}',
    'anki.url': 'Адрес',
    'anki.version': 'Версия протокола',
    'anki.linked': 'Связано заметок',
    'anki.lastSync': 'Последняя отправка',
    'anki.never': 'ещё не было',
    'anki.dryRun': 'Пробный прогон',
    'anki.dryRunHint': 'Показывает, что будет отправлено, и ничего не меняет.',
    'anki.dryRunUnsupported':
      'Этот бэкенд не объявляет dry_run, поэтому предпросмотр недоступен: отправка сразу запишет карточки в Anki.',
    'anki.sync': 'Отправить в Anki',
    'anki.syncing': 'Отправка…',
    'anki.confirmSync': 'Отправить все сохранённые слова в Anki?',
    'anki.resultCreated': 'Создано',
    'anki.resultUpdated': 'Обновлено',
    'anki.resultUnchanged': 'Без изменений',
    'anki.resultFailed': 'Ошибок',
    'anki.firstNotes': 'Первые карточки',
    'anki.noNotes': 'Нечего отправлять.',
    'anki.syncErrors': 'Ошибки отправки',
    'error.network': 'Сервер не отвечает. Проверьте адрес и подключение.',
    'error.unauthorized': 'Требуется вход.',
    'error.forbidden': 'Недостаточно прав.',
    'error.notFound': 'Запись не найдена.',
    'error.validation': 'Проверьте введённые данные.',
    'error.server': 'Ошибка сервера. Попробуйте позже.',
    'error.unknown': 'Что-то пошло не так.',
    'error.status': 'HTTP {status}',
  },
  en: {
    'app.name': 'OpenLanguageReactor',
    'app.tagline': 'Self-hosted subtitle trainer',
    'app.bootFailed': 'Could not reach the server.',
    'app.bootHint': 'Check that the API is running and reachable at this address, then retry.',
    'nav.vocab': 'Vocabulary',
    'nav.settings': 'Settings',
    'nav.anki': 'Anki',
    'nav.logout': 'Log out',
    'nav.lang': 'Interface language',
    'common.loading': 'Loading…',
    'common.save': 'Save',
    'common.saving': 'Saving…',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.close': 'Close',
    'common.retry': 'Retry',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.optional': 'optional',
    'common.none': 'none',
    'common.unknown': 'unknown',
    'common.saved': 'Saved',
    'common.of': 'of',
    'common.notFound': 'Page not found',
    'common.notFoundHint': 'No such section. Open the vocabulary or settings.',
    'health.ok': 'Server reachable',
    'health.fail': 'Server unreachable',
    'health.checking': 'Checking server…',
    'health.base': 'Server address',
    'auth.login': 'Log in',
    'auth.register': 'Register',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.displayName': 'Name (optional)',
    'auth.loginAction': 'Log in',
    'auth.registerAction': 'Create account',
    'auth.loggingIn': 'Logging in…',
    'auth.registering': 'Creating account…',
    'auth.haveAccount': 'Already have an account? Log in',
    'auth.noAccount': 'No account? Register',
    'auth.registerDisabled':
      'Registration is closed on this server. Ask the administrator to create an account or enable OLR_ALLOW_REGISTRATION.',
    'auth.invalidCredentials': 'Wrong email or password.',
    'auth.emailTaken': 'An account with that email already exists.',
    'auth.passwordTooShort': 'The password must be at least 8 characters.',
    'auth.sessionExpired': 'Session expired. Please log in again.',
    'auth.loggedOut': 'Logged out.',
    'vocab.title': 'Vocabulary',
    'vocab.subtitle': 'Words and phrases saved by the extension or added here.',
    'vocab.search': 'Search word or translation',
    'vocab.searchHint': 'Search runs on the server; the page is also filtered locally.',
    'vocab.filterSource': 'Source language',
    'vocab.filterTarget': 'Target language',
    'vocab.filterAll': 'all',
    'vocab.sort': 'Sort',
    'vocab.sortUpdatedDesc': 'Recently updated',
    'vocab.sortAlpha': 'Alphabetical',
    'vocab.sortCreatedDesc': 'Newest first',
    'vocab.sortCreatedAsc': 'Oldest first',
    'vocab.colWord': 'Word',
    'vocab.colTranslation': 'Translation',
    'vocab.colContext': 'Context',
    'vocab.colNote': 'Note',
    'vocab.colLangs': 'Languages',
    'vocab.colUpdated': 'Updated',
    'vocab.colActions': 'Actions',
    'vocab.empty': 'Nothing here yet',
    'vocab.emptyHint': 'Save a word from the extension or add one here.',
    'vocab.noMatches': 'No rows match the current filter.',
    'vocab.add': 'Add word',
    'vocab.addTitle': 'New word',
    'vocab.added': 'Word added',
    'vocab.updated': 'Changes saved',
    'vocab.deleted': 'Deleted',
    'vocab.confirmDelete': 'Delete “{word}”? This cannot be undone.',
    'vocab.confirmBulk': 'Delete {count} selected rows?',
    'vocab.selected': '{count} selected',
    'vocab.selectAll': 'Select all on page',
    'vocab.clearSelection': 'Clear selection',
    'vocab.bulkDelete': 'Delete selected',
    'vocab.page': 'Page {page} of {pages}',
    'vocab.prev': 'Previous',
    'vocab.next': 'Next',
    'vocab.total': '{count} entries',
    'vocab.import': 'Import',
    'vocab.importHint': 'CSV, TSV or JSON. The first CSV row is a header (text, translation, context).',
    'vocab.importAction': 'Upload file',
    'vocab.importResult': 'Imported: {imported}, skipped: {skipped}',
    'vocab.importErrors': 'Errors: {count}',
    'vocab.export': 'Export',
    'vocab.exportEmpty': 'Nothing to export.',
    'vocab.occurrences': 'seen: {count}',
    'vocab.wordRequired': 'The word cannot be empty.',
    'vocab.fieldWord': 'Word',
    'vocab.fieldTranslation': 'Translation',
    'vocab.fieldNote': 'Note',
    'settings.title': 'Settings',
    'settings.subtitle': 'Applied to the extension on every device of this account.',
    'settings.languages': 'Languages',
    'settings.source': 'Source language',
    'settings.target': 'Target language',
    'settings.interface': 'Extension interface language',
    'settings.provider': 'Translation provider',
    'settings.playback': 'Subtitles and playback',
    'settings.fontScale': 'Subtitle size',
    'settings.opacity': 'Background opacity',
    'settings.autoPause': 'Pause at the end of a line',
    'settings.showNative': 'Show native transcription',
    'settings.autoSave': 'Save words automatically',
    'settings.anki': 'Anki',
    'settings.ankiEnabled': 'Send words to Anki',
    'settings.deck': 'Deck',
    'settings.model': 'Model',
    'settings.tags': 'Tags',
    'settings.updatedAt': 'Updated: {date}',
    'settings.loadError': 'Could not load settings.',
    'anki.title': 'Anki',
    'anki.subtitle': 'AnkiConnect connectivity and card export.',
    'anki.notice':
      'AnkiConnect runs on the user’s computer (127.0.0.1:8765), not on the server. Unless Anki runs on the server itself, “unreachable” is the expected result.',
    'anki.status': 'Status',
    'anki.ping': 'Check connection',
    'anki.pinging': 'Checking…',
    'anki.reachable': 'AnkiConnect reachable',
    'anki.unreachable': 'AnkiConnect unreachable',
    'anki.reason': 'Reason: {reason}',
    'anki.url': 'Address',
    'anki.version': 'Protocol version',
    'anki.linked': 'Linked notes',
    'anki.lastSync': 'Last push',
    'anki.never': 'never',
    'anki.dryRun': 'Dry run',
    'anki.dryRunHint': 'Shows what would be sent without changing anything.',
    'anki.dryRunUnsupported':
      'This backend does not declare dry_run, so preview is unavailable: pushing will write cards to Anki immediately.',
    'anki.sync': 'Push to Anki',
    'anki.syncing': 'Pushing…',
    'anki.confirmSync': 'Push every saved word to Anki?',
    'anki.resultCreated': 'Created',
    'anki.resultUpdated': 'Updated',
    'anki.resultUnchanged': 'Unchanged',
    'anki.resultFailed': 'Failed',
    'anki.firstNotes': 'First notes',
    'anki.noNotes': 'Nothing to push.',
    'anki.syncErrors': 'Sync errors',
    'error.network': 'Server is not responding. Check the address and connection.',
    'error.unauthorized': 'Log in required.',
    'error.forbidden': 'Not enough permissions.',
    'error.notFound': 'Entry not found.',
    'error.validation': 'Check the entered data.',
    'error.server': 'Server error. Try again later.',
    'error.unknown': 'Something went wrong.',
    'error.status': 'HTTP {status}',
  },
} as const;

export type TranslationKey = keyof (typeof translations)['ru'];

type Listener = (lang: Lang) => void;
const listeners = new Set<Listener>();

function readStoredLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'ru' || stored === 'en') return stored;
  } catch {
    // Private mode / disabled storage: fall through to the default.
  }
  return 'ru';
}

let current: Lang = typeof localStorage === 'undefined' ? 'ru' : readStoredLang();

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  if (lang === current) return;
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Persisting the preference is best-effort.
  }
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
  for (const listener of listeners) listener(lang);
}

export function onLangChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function t(key: TranslationKey, vars?: Record<string, string | number>): string {
  const table = translations[current] as Record<string, string>;
  const fallback = translations.ru as Record<string, string>;
  let value = table[key] ?? fallback[key] ?? key;
  if (vars) {
    for (const [name, replacement] of Object.entries(vars)) {
      value = value.replaceAll(`{${name}}`, String(replacement));
    }
  }
  return value;
}

/** Russian plural form: 1 слово, 2 слова, 5 слов. */
export function pluralRu(count: number, forms: readonly [string, string, string]): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

/** Format a count with the right noun for the active language. */
export function entryCount(count: number): string {
  if (current === 'ru') return `${count} ${pluralRu(count, ['запись', 'записи', 'записей'])}`;
  return count === 1 ? '1 entry' : `${count} entries`;
}
